import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/apiClient';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  UsersIcon,
  CreditCardIcon,
  ClockIcon,
  StarIcon,
  XCircleIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

interface TenantInfo {
  tenant_id: string;
  owner_email: string;
  owner_user_id: string;
  tenant_name: string;
  plan_id: string;
  plan_status: string;
  trial_ends_at?: string;
  created_at: string;
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
  usage_stats: {
    customers_count: number;
    sources_count: number;
    ai_queries_count: number;
    user_seats_count: number;
  };
}

interface AdminStats {
  total_tenants: number;
  active_subscriptions: number;
  trial_tenants: number;
  pilot_tenants: number;
  expired_trials: number;
  revenue_metrics: {
    monthly_recurring_revenue: number;
    annual_recurring_revenue: number;
    average_revenue_per_user: number;
  };
}

const AdminPage: React.FC = () => {
  const [selectedTenant, setSelectedTenant] = useState<TenantInfo | null>(null);
  const [planFilter, setPlanFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [adminAssignmentTenantId, setAdminAssignmentTenantId] = useState<string>('');
  const queryClient = useQueryClient();

  // Fetch admin stats
  const { data: stats, isLoading: statsLoading } = useQuery<AdminStats>({
    queryKey: ['admin-stats'],
    queryFn: () => apiClient.customRequest('/api/admin/stats'),
  });

  // Fetch tenants list
  const { data: tenants, isLoading: tenantsLoading, refetch } = useQuery<TenantInfo[]>({
    queryKey: ['admin-tenants', planFilter, statusFilter],
    queryFn: () => {
      const params = new URLSearchParams();
      if (planFilter) params.append('plan_filter', planFilter);
      if (statusFilter) params.append('status_filter', statusFilter);
      return apiClient.customRequest(`/api/admin/tenants?${params.toString()}`);
    },
  });

  // Approve pilot mutation
  const approvePilotMutation = useMutation({
    mutationFn: (tenantId: string) => 
      apiClient.customRequest(`/api/admin/tenants/${tenantId}/approve-pilot`, {
        method: 'POST',
        body: { tenant_id: tenantId, notes: 'Approved via admin panel' }
      }) as Promise<{ message: string }>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tenants'] });
      queryClient.invalidateQueries({ queryKey: ['admin-stats'] });
    },
  });

  // Revoke pilot mutation
  const revokePilotMutation = useMutation({
    mutationFn: (tenantId: string) => 
      apiClient.customRequest(`/api/admin/tenants/${tenantId}/revoke-pilot`, {
        method: 'POST'
      }) as Promise<{ message: string }>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tenants'] });
      queryClient.invalidateQueries({ queryKey: ['admin-stats'] });
    },
  });

  // Admin plan assignment mutation
  const assignAdminPlanMutation = useMutation({
    mutationFn: (tenantId: string) => 
      apiClient.assignAdminPlan(tenantId) as Promise<{ status: string; message: string }>,
    onSuccess: (data) => {
      window.alert(`✅ ${data.message}`);
      setAdminAssignmentTenantId('');
      queryClient.invalidateQueries({ queryKey: ['admin-tenants'] });
      queryClient.invalidateQueries({ queryKey: ['admin-stats'] });
    },
    onError: (error: any) => {
      window.alert(`❌ Failed to assign admin plan: ${error.message}`);
    },
  });

  const getPlanBadgeColor = (planId: string) => {
    switch (planId) {
      case 'free_trial': return 'bg-muted text-muted-foreground';
      case 'pilot': return 'bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-200';
      case 'startup': return 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200';
      case 'business': return 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200';
      case 'admin': return 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200';
      case 'trialing': return 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200';
      case 'expired': return 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-200';
      case 'past_due': return 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200';
      case 'canceled': return 'bg-muted text-muted-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (statsLoading || tenantsLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="space-y-6 p-6">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold tracking-tight text-foreground">Admin Panel</h1>
          <p className="text-muted-foreground">Manage tenants, subscriptions, and pilot programs.</p>
        </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          <Card className="bg-card/80 backdrop-blur-sm border border-border/20 shadow-lg hover:shadow-xl transition-all duration-200">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Tenants</CardTitle>
              <UsersIcon className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-foreground">{stats.total_tenants}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Subscriptions</CardTitle>
              <CreditCardIcon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.active_subscriptions}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Trial Tenants</CardTitle>
              <ClockIcon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.trial_tenants}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pilot Tenants</CardTitle>
              <StarIcon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.pilot_tenants}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">MRR</CardTitle>
              <CreditCardIcon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(stats.revenue_metrics.monthly_recurring_revenue)}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="bg-card/80 backdrop-blur-sm border-border/20 shadow-lg">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-foreground">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex space-x-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-muted-foreground">Plan</label>
              <select
                value={planFilter}
                onChange={(e) => setPlanFilter(e.target.value)}
                className="border border-border rounded-lg px-3 py-2 bg-background/90 backdrop-blur-sm focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-200"
              >
                <option value="">All Plans</option>
                <option value="free_trial">Free Trial</option>
                <option value="pilot">Pilot</option>
                <option value="startup">Startup</option>
                <option value="business">Business</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-muted-foreground">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border border-border rounded-lg px-3 py-2 bg-background/90 backdrop-blur-sm focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-200"
              >
                <option value="">All Statuses</option>
                <option value="trialing">Trialing</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="past_due">Past Due</option>
                <option value="canceled">Canceled</option>
              </select>
            </div>
            <div className="flex items-end">
              <Button onClick={() => refetch()} variant="outline">
                Refresh
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tenants Table */}
      <Card>
        <CardHeader>
          <CardTitle>Tenants</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Tenant</th>
                  <th className="text-left p-2">Plan</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-left p-2">Usage</th>
                  <th className="text-left p-2">Created</th>
                  <th className="text-left p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tenants?.map((tenant) => (
                  <tr key={tenant.tenant_id} className="border-b hover:bg-muted/50">
                    <td className="p-2">
                      <div>
                        <div className="font-medium text-foreground">{tenant.tenant_name}</div>
                        <div className="text-sm text-muted-foreground">{tenant.owner_email}</div>
                        <div className="text-xs text-muted-foreground">{tenant.tenant_id}</div>
                      </div>
                    </td>
                    <td className="p-2">
                      <Badge className={getPlanBadgeColor(tenant.plan_id)}>
                        {tenant.plan_id.replace('_', ' ').toUpperCase()}
                      </Badge>
                    </td>
                    <td className="p-2">
                      <Badge className={getStatusBadgeColor(tenant.plan_status)}>
                        {tenant.plan_status.toUpperCase()}
                      </Badge>
                    </td>
                    <td className="p-2">
                      <div className="text-sm text-foreground">
                        <div>Customers: {tenant.usage_stats.customers_count}</div>
                        <div>Sources: {tenant.usage_stats.sources_count}</div>
                        <div>Queries: {tenant.usage_stats.ai_queries_count}</div>
                      </div>
                    </td>
                    <td className="p-2">
                      <div className="text-sm text-foreground">{formatDate(tenant.created_at)}</div>
                      {tenant.trial_ends_at && (
                        <div className="text-xs text-muted-foreground">
                          Trial ends: {formatDate(tenant.trial_ends_at)}
                        </div>
                      )}
                    </td>
                    <td className="p-2">
                      <div className="flex space-x-2">
                        {tenant.plan_id === 'free_trial' && tenant.plan_status !== 'expired' && (
                          <Button
                            size="sm"
                            onClick={() => approvePilotMutation.mutate(tenant.tenant_id)}
                            disabled={approvePilotMutation.isPending}
                            className="bg-purple-600 hover:bg-purple-700 text-white"
                          >
                            <StarIcon className="w-3 h-3 mr-1" />
                            Approve Pilot
                          </Button>
                        )}
                        {tenant.plan_id === 'pilot' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => revokePilotMutation.mutate(tenant.tenant_id)}
                            disabled={revokePilotMutation.isPending}
                            className="text-destructive border-destructive hover:bg-destructive/10"
                          >
                            <XCircleIcon className="w-3 h-3 mr-1" />
                            Revoke Pilot
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSelectedTenant(tenant)}
                        >
                          <EyeIcon className="w-3 h-3 mr-1" />
                          View
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Tenant Details Modal */}
      {selectedTenant && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Tenant Details</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedTenant(null)}
                >
                  ×
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-foreground">Basic Information</h3>
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div>
                      <label className="text-sm text-muted-foreground">Tenant Name</label>
                      <div className="text-foreground">{selectedTenant.tenant_name}</div>
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Owner Email</label>
                      <div className="text-foreground">{selectedTenant.owner_email}</div>
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Plan</label>
                      <div>
                        <Badge className={getPlanBadgeColor(selectedTenant.plan_id)}>
                          {selectedTenant.plan_id.replace('_', ' ').toUpperCase()}
                        </Badge>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Status</label>
                      <div>
                        <Badge className={getStatusBadgeColor(selectedTenant.plan_status)}>
                          {selectedTenant.plan_status.toUpperCase()}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-foreground">Usage Statistics</h3>
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div>
                      <label className="text-sm text-muted-foreground">Customers</label>
                      <div className="text-foreground">{selectedTenant.usage_stats.customers_count}</div>
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Data Sources</label>
                      <div className="text-foreground">{selectedTenant.usage_stats.sources_count}</div>
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">AI Queries</label>
                      <div className="text-foreground">{selectedTenant.usage_stats.ai_queries_count}</div>
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">User Seats</label>
                      <div className="text-foreground">{selectedTenant.usage_stats.user_seats_count}</div>
                    </div>
                  </div>
                </div>

                {selectedTenant.stripe_customer_id && (
                  <div>
                    <h3 className="font-semibold text-foreground">Billing Information</h3>
                    <div className="grid grid-cols-1 gap-2 mt-2">
                      <div>
                        <label className="text-sm text-muted-foreground">Stripe Customer ID</label>
                        <div className="font-mono text-sm text-foreground">{selectedTenant.stripe_customer_id}</div>
                      </div>
                      {selectedTenant.stripe_subscription_id && (
                        <div>
                          <label className="text-sm text-muted-foreground">Stripe Subscription ID</label>
                          <div className="font-mono text-sm text-foreground">{selectedTenant.stripe_subscription_id}</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Admin Plan Assignment Section */}
      <Card className="bg-card/80 backdrop-blur-sm border-border/20 shadow-lg hover:shadow-xl transition-all duration-200">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <StarIcon className="h-5 w-5 text-yellow-500" />
            <span className="text-foreground">Admin Plan Assignment</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <div className="flex items-start">
                <StarIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-3" />
                <div>
                  <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">Admin Plan Features</h3>
                  <ul className="mt-2 text-sm text-yellow-700 dark:text-yellow-300 list-disc list-inside space-y-1">
                    <li>Unlimited customers, data sources, and AI queries</li>
                    <li>Unlimited team members and data retention</li>
                    <li>Admin panel access and platform management</li>
                    <li>Priority support and special privileges</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Tenant ID to Assign Admin Plan
                </label>
                <input
                  type="text"
                  value={adminAssignmentTenantId}
                  onChange={(e) => setAdminAssignmentTenantId(e.target.value)}
                  placeholder="e.g., tenant_ZQnwl85c"
                  className="w-full border border-border rounded-lg px-3 py-2 bg-background/90 backdrop-blur-sm focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-200"
                />
              </div>
              <div className="flex items-end">
                <Button
                  onClick={() => {
                    if (!adminAssignmentTenantId.trim()) {
                      window.alert('Please enter a tenant ID');
                      return;
                    }
                    if (window.confirm(`Are you sure you want to assign admin plan to ${adminAssignmentTenantId}? This will give them unlimited access to everything.`)) {
                      assignAdminPlanMutation.mutate(adminAssignmentTenantId);
                    }
                  }}
                  disabled={assignAdminPlanMutation.isPending || !adminAssignmentTenantId.trim()}
                  className="bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
                >
                  {assignAdminPlanMutation.isPending ? (
                    <div className="flex items-center space-x-2">
                      <LoadingSpinner size="sm" />
                      <span>Assigning...</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <StarIcon className="h-4 w-4" />
                      <span>Assign Admin Plan</span>
                    </div>
                  )}
                </Button>
              </div>
            </div>
            
            <div className="text-sm text-muted-foreground">
              <p><strong>Note:</strong> Only admin users can assign admin plans. The assigned tenant will immediately receive unlimited access to all platform features.</p>
            </div>
          </div>
        </CardContent>
      </Card>
      </div>
    </div>
  );
};

export default AdminPage;
